
import { defineComponent } from 'vue'
import { AxiosResponse } from 'axios'
import DocumentBrowserHint from './DocumentBrowserHint.vue'
import DocumentBrowserTree from './DocumentBrowserTree.vue'
import DocumentBrowserDetail from './DocumentBrowserDetail.vue'
import KnFabButton from '@/components/UI/KnFabButton.vue'
import Menu from 'primevue/menu'

export default defineComponent({
    name: 'document-browser-home',
    components: { DocumentBrowserHint, DocumentBrowserTree, DocumentBrowserDetail, KnFabButton, Menu },
    props: { documentSaved: { type: Object }, documentSavedTrigger: { type: Boolean } },
    emits: ['itemSelected'],
    data() {
        return {
            folders: [] as any[],
            selectedFolder: null as any,
            documents: [] as any[],
            searchedDocuments: [] as any[],
            breadcrumbs: [] as any[],
            selectedBreadcrumb: null as any,
            searchWord: null as any,
            searchMode: false,
            items: [] as any[],
            user: null as any,
            sidebarVisible: false,
            windowWidth: window.innerWidth,
            loading: false,
            showDocumentDetails: false,
            selectedDocument: null as any,
            documentId: null as any
        }
    },
    computed: {
        isSuperAdmin(): boolean {
            return this.user?.isSuperadmin
        },
        canAddNewDocument(): boolean {
            return this.user?.functionalities.includes('DocumentManagement')
        },
        hasCreateCockpitFunctionality(): boolean {
            return this.user.functionalities.includes('CreateCockpitFunctionality')
        },
        isSidebarHidden(): boolean {
            if (this.sidebarVisible) {
                return false
            } else {
                return this.windowWidth < 1024
            }
        }
    },
    watch: {
        documentSavedTrigger() {
            if (!this.documentSaved) return

            if (this.documentSaved.folderId) this.selectedFolder.id = this.documentSaved.folderId
            this.loadDocumentsWithBreadcrumbs()
        }
    },
    async created() {
        window.addEventListener('resize', this.onResize)

        await this.loadFolders()
        this.user = (this.$store.state as any).user

        if (this.$route.name === 'document-browser-functionality') {
            this.setFolderFromRoute()
        }

        this.setRouterWatcher()
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize)
    },
    methods: {
        setRouterWatcher() {
            this.$watch(
                () => this.$route.params.pathMatch,
                (toParams) => {
                    if (this.$route.name === 'document-browser-functionality' && toParams.length > 0) this.setFolderFromRoute()
                }
            )
        },
        onResize() {
            this.windowWidth = window.innerWidth
        },
        async setFolderFromRoute() {
            if (this.$route.params.pathMatch.length > 0) {
                this.selectedFolder = this.findSelectedFolder()
                if (!this.selectedFolder) return
                localStorage.setItem('documentSelectedFolderId', JSON.stringify(this.selectedFolder.id))
                await this.loadDocumentsWithBreadcrumbs()
            }
        },
        findSelectedFolder() {
            const id = this.$route.params.pathMatch[this.$route.params.pathMatch.length - 1]
            const index = this.folders.findIndex((folder: any) => folder.id == id)
            return index !== -1 ? this.folders[index] : null
        },
        async loadFolders() {
            this.loading = true
            await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `2.0/folders/`).then((response: AxiosResponse<any>) => {
                this.folders = response.data
                this.folders?.sort((a: any, b: any) => {
                    return a.id - b.id
                })
            })

            this.loading = false
        },
        async loadDocuments() {
            this.loading = true
            const url = this.searchMode ? `2.0/documents?searchAttributes=all&searchKey=${this.searchWord}` : `2.0/documents?folderId=${this.selectedFolder?.id}`
            await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + url).then((response: AxiosResponse<any>) => {
                this.searchMode ? (this.searchedDocuments = response.data) : (this.documents = response.data)
            })
            this.loading = false
        },
        async setSelectedFolder(folder: any) {
            if (this.selectedFolder?.id === folder.id) {
                return
            }
            this.selectedFolder = folder
            this.changeFolderRotue()
            await this.loadDocumentsWithBreadcrumbs()
        },
        changeFolderRotue() {
            const tempPath = this.selectedFolder.path?.substring(1)?.split('/')
            if (!tempPath) return
            let temp = ''
            for (let i = 0; i < tempPath.length; i++) {
                const index = this.folders.findIndex((folder: any) => folder.code == tempPath[i])
                if (index !== -1) {
                    temp += `/${this.folders[index].id}`
                }
            }
            history.pushState({}, '', process.env.VUE_APP_PUBLIC_PATH + 'document-browser' + temp)
        },
        async loadDocumentsWithBreadcrumbs() {
            if (this.selectedFolder && this.selectedFolder.id !== -1) {
                await this.loadDocuments()
                this.createBreadcrumbs()
            } else {
                this.documents = []
            }
        },
        createBreadcrumbs() {
            let currentFolder = { key: this.selectedFolder.name, label: this.selectedFolder.name, data: this.selectedFolder } as any
            this.breadcrumbs = [] as any[]
            do {
                this.breadcrumbs.unshift({ label: currentFolder.data.name, node: currentFolder })
                currentFolder = currentFolder.data.parentFolder
            } while (currentFolder)
        },
        async setSelectedBreadcrumb(breadcrumb: any) {
            this.selectedBreadcrumb = breadcrumb

            if (this.selectedFolder?.id === breadcrumb.node.data.id) {
                return
            }
            this.selectedFolder = breadcrumb.node.data
            await this.loadDocuments()
        },
        exitSearchMode() {
            this.searchMode = false
        },
        toggle(event: any) {
            this.createMenuItems()
            const menu = this.$refs.menu as any
            menu.toggle(event)
        },
        createMenuItems() {
            this.items = []
            this.items.push({ label: this.$t('documentBrowser.genericDocument'), command: () => this.createNewDocument() })
            if (this.hasCreateCockpitFunctionality) {
                this.items.push({ label: this.$t('common.cockpit'), command: () => this.createNewCockpit() })
            }
        },
        createNewDocument() {
            this.documentId = null
            this.$emit('itemSelected', { item: null, mode: 'documentDetail', functionalityId: this.selectedFolder.id })
        },
        async openDocumentDetails(event) {
            this.documentId = event.id
            this.$emit('itemSelected', { item: event, mode: 'documentDetail', functionalityId: null })
        },
        createNewCockpit() {
            this.$emit('itemSelected', { item: null, mode: 'createCockpit', functionalityId: this.selectedFolder.id })
        },
        toggleSidebarView() {
            this.sidebarVisible = !this.sidebarVisible
        },
        openSearchBar() {
            this.searchMode = true
            setTimeout(() => {
                // @ts-ignore
                this.$refs.searchBar.$el.focus()
            }, 0)
        },
        onCloseDetails() {
            this.showDocumentDetails = false
            this.loadDocuments()
        }
    }
})
